import * as ngCore from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'

import * as helpers from './helpers'
import * as models from './models'
import { State } from './state'

/** Horizontal space reserved for the header/cell chrome during auto-fit. */
const COLUMN_FIT_CHROME_PX = 30
/** Width reserved for the optional selection checkbox column. */
const SELECTION_COLUMN_WIDTH_PX = 30

/**
 * Manages the prepared table view and interaction state for one component
 * instance. Consumer-owned behavior remains in the current config snapshot.
 *
 * The signal graph intentionally has two layers:
 *
 * 1. Config section signals expose the references supplied by the consumer.
 * 2. Preparation signals derive rows, columns, widths, and status from only
 *    the sections they need.
 *
 * Keeping those dependencies separate is important for entity-collection
 * tables. A page change should replace rows and pagination, but it should not
 * repeat column ordering, width reconciliation, or layout work.
 */
@ngCore.Injectable()
export class Store<TRow = unknown> extends ComponentStore<State<TRow>> {
  /**
   * Latest immutable consumer snapshot.
   *
   * Section selectors deliberately return existing branch references. Angular
   * computed signals use reference equality, so a top-level snapshot that only
   * changes status can stop before invalidating row, tool, or layout
   * preparation.
   */
  private readonly _configSg = this.selectSignal(state => state.config)

  /** Tool branch isolated from row and layout preparation. */
  private readonly _toolsConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.tools,
  )

  /** Column branch shared by ordering, sizing, and cell preparation. */
  private readonly _columnsConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.columns ?? [],
  )

  /** Hierarchy branch shared by parent normalization and visibility. */
  private readonly _hierarchyConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.hierarchy,
  )

  /** Layout branch isolated from source-row and cell preparation. */
  private readonly _layoutConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.layout,
  )

  /** Pagination branch isolated from row and layout preparation. */
  public readonly paginationConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.pagination,
  )

  /** Row branch used as the sole source for prepared row identities. */
  private readonly _rowsConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.rows,
  )

  /** Search branch isolated from row and layout preparation. */
  public readonly searchConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.search,
  )

  /** Selection branch shared by layout and prepared rows. */
  public readonly selectionConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.selection,
  )

  /** Request and empty-state branch isolated from row preparation. */
  private readonly _statusConfigSg = ngCore.computed(
    (config = this._configSg()) => config?.status,
  )

  /** @see {@link State.columnContentWidthsPx} */
  private readonly _columnContentWidthsPxSg = this.selectSignal(
    state => state.columnContentWidthsPx,
  )

  /** @see {@link State.columnOrder} */
  private readonly _columnOrderSg = this.selectSignal(
    state => state.columnOrder,
  )

  /** @see {@link State.expandedRowIds} */
  private readonly _expandedRowIdsSg = this.selectSignal(
    state => state.expandedRowIds,
  )

  /** @see {@link State.horizontalScrollLeftPx} */
  private readonly _horizontalScrollLeftPxSg = this.selectSignal(
    state => state.horizontalScrollLeftPx,
  )

  /** @see {@link State.hostWidthPx} */
  private readonly _hostWidthPxSg = this.selectSignal(
    state => state.hostWidthPx,
  )

  /** @see {@link State.resizedColumnWidthsPx} */
  private readonly _resizedColumnWidthsPxSg = this.selectSignal(
    state => state.resizedColumnWidthsPx,
  )

  /** Whether the settings overlay is currently visible. */
  public readonly settingsOpenSg = this.selectSignal(
    state => state.settingsOpen,
  )

  /** Whether cell content may wrap onto multiple lines. */
  public readonly wrapSg = this.selectSignal(state => state.wrap)

  /** Standard tools rendered above the table data. */
  public readonly toolbarToolsSg = ngCore.computed(
    (tools = this._toolsConfigSg()) => [
      ...(tools?.create ? [tools.create] : []),
      ...(tools?.toolbar ?? []),
    ],
  )

  /** Configured container style. */
  public readonly containerSg = ngCore.computed(
    (layout = this._layoutConfigSg()) => layout?.container ?? 'widget',
  )

  /** Configured table title. */
  public readonly titleSg = ngCore.computed(
    (layout = this._layoutConfigSg()) => layout?.title ?? 'Table',
  )

  /** Whether the widget exposes its column settings action. */
  public readonly showColumnSettingsSg = ngCore.computed(
    (layout = this._layoutConfigSg()) => layout?.showColumnSettings ?? true,
  )

  /** Whether rows use the hierarchy relation. */
  public readonly hasHierarchySg = ngCore.computed(
    (hierarchy = this._hierarchyConfigSg()) => hierarchy !== undefined,
  )

  /**
   * Rows after applying the consumer's row adapter.
   *
   * Hierarchy parent IDs are normalized here, before expansion and visibility
   * are calculated. This keeps the later row helpers independent of the
   * consumer's row-config shape.
   */
  private readonly _sourceRowsSg = ngCore.computed(
    (
      rowsConfig = this._rowsConfigSg(),
      hierarchy = this._hierarchyConfigSg(),
    ) => helpers.Row.getSource(rowsConfig, hierarchy),
  )

  /**
   * Pagination values prepared for direct rendering.
   *
   * The helper clamps invalid controlled values and derives page buttons only
   * when a total count is available. Unknown-count sources still receive the
   * explicit next/previous state supplied by their adapter.
   */
  public readonly paginationSg = ngCore.computed(
    (pagination = this.paginationConfigSg()) =>
      helpers.Pagination.get(pagination),
  )

  /**
   * Configured columns in the current local order.
   *
   * `columnOrder` contains only local UI state and stays `undefined` until the
   * user reorders columns, so the configured order is used as-is by default.
   * The controlled config remains the source of truth for which columns exist;
   * once a local order exists, the helper inserts newly configured IDs next to
   * their configured predecessor and drops removed IDs.
   */
  public readonly orderedColumnsSg = ngCore.computed(
    (columns = this._columnsConfigSg(), columnOrder = this._columnOrderSg()) =>
      helpers.Column.getOrdered(columns, columnOrder),
  )

  /** Visible columns in the current local order. */
  private readonly _visibleColumnsSg = ngCore.computed(
    (columns = this.orderedColumnsSg()) => helpers.Column.getVisible(columns),
  )

  /**
   * Source rows after applying the current expansion state.
   *
   * The expansion set is consulted only after the source rows have been
   * normalized. A malformed parent cycle is handled by the row helper's active
   * path guard rather than being allowed to recurse indefinitely.
   */
  private readonly _visibleSourceRowsSg = ngCore.computed(
    (
      sourceRows = this._sourceRowsSg(),
      hierarchy = this._hierarchyConfigSg(),
      expandedRowIds = this._expandedRowIdsSg(),
    ) => helpers.Row.getVisible(sourceRows, hierarchy, expandedRowIds),
  )

  /** Width reserved for the largest configured row-tool set. */
  public readonly rowToolsWidthPxSg = ngCore.computed(
    (rows = this._sourceRowsSg()) => helpers.Row.getToolsWidth(rows),
  )

  /** Left position of hovered row tools relative to their scrolled row. */
  public readonly rowToolsOffsetLeftPxSg = ngCore.computed(
    (
      horizontalScrollLeftPx = this._horizontalScrollLeftPxSg(),
      hostWidthPx = this._hostWidthPxSg(),
      rowToolsWidthPx = this.rowToolsWidthPxSg(),
    ) => horizontalScrollLeftPx + hostWidthPx - rowToolsWidthPx,
  )

  /** Width reserved by row-selection controls when configured. */
  public readonly selectionWidthPxSg = ngCore.computed(
    (selection = this.selectionConfigSg()) =>
      selection ? SELECTION_COLUMN_WIDTH_PX : 0,
  )

  /**
   * Shared column dimensions for the header and every row.
   *
   * Both header and body consume this one layout so their grid tracks cannot
   * drift apart. Widths include measured content, manual resize state,
   * selection chrome, and row-tool chrome.
   */
  public readonly layoutSg = ngCore.computed(
    (
      columns = this._visibleColumnsSg(),
      hostWidthPx = this._hostWidthPxSg(),
      resizedWidthsPx = this._resizedColumnWidthsPxSg(),
      selectionWidthPx = this.selectionWidthPxSg(),
      rowToolsWidthPx = this.rowToolsWidthPxSg(),
    ) =>
      helpers.Column.getLayout(
        columns,
        hostWidthPx,
        resizedWidthsPx,
        selectionWidthPx,
        rowToolsWidthPx,
      ),
  )

  /**
   * Visible rows prepared for direct template binding.
   *
   * Cell callbacks are executed here, after hierarchy visibility and column
   * layout have been resolved. Templates therefore bind plain prepared values
   * instead of running consumer callbacks or cell calculations themselves.
   */
  public readonly rowsSg = ngCore.computed(
    (
      columns = this.layoutSg().columns,
      visibleRows = this._visibleSourceRowsSg(),
      selection = this.selectionConfigSg(),
    ) => helpers.Row.getApplied(columns, visibleRows, selection),
  )

  /**
   * Loading, error, or empty presentation replacing the data view.
   *
   * Status is derived from the source status, create tool, and normalized
   * source-row count. Loading and error take precedence over empty content.
   */
  public readonly statusSg = ngCore.computed(
    (
      status = this._statusConfigSg(),
      tools = this._toolsConfigSg(),
      rows = this._sourceRowsSg(),
    ) => helpers.Status.get(status, tools?.create, rows.length),
  )

  constructor() {
    super(State.INITIAL)
  }

  /**
   * Applies a complete config snapshot. Width and order state survives only
   * for column IDs that still exist in the new snapshot; a missing input
   * resets the view-scoped state.
   */
  public setConfig(config: models.Config<TRow> | undefined): void {
    if (config === undefined) {
      // Clearing the input also clears view-local order, widths, expansion,
      // and wrapping state; none of those values belong to a missing table.
      this.setState(State.INITIAL)
      return
    }

    this.setState(state => {
      if (state.config?.columns === config.columns) {
        // A new top-level snapshot may update rows, pagination, or status while
        // retaining the exact column-array reference. In that common case the
        // existing order and width maps are already valid, so avoid rebuilding
        // them. This is reference comparison by design; configs are immutable.
        return {
          ...state,
          config,
          wrap:
            state.config === undefined ||
            (state.config.layout?.wrap ?? false) !==
              (config.layout?.wrap ?? false)
              ? (config.layout?.wrap ?? false)
              : state.wrap,
        }
      }

      // A new column-array reference may add, remove, or reorder IDs. Preserve
      // measurements and local ordering only for IDs that still exist.
      const configuredColumnIds = config.columns.map(column => column.id)
      const configuredColumnIdSet = new Set(configuredColumnIds)

      return {
        ...state,
        columnContentWidthsPx: helpers.Column.filterWidths(
          state.columnContentWidthsPx,
          configuredColumnIdSet,
        ),
        // Only a user-defined order is reconciled. Without one, the configured
        // order stays authoritative, so columns that arrive later (e.g. once
        // async attributes resolve) keep their configured position.
        columnOrder:
          state.columnOrder &&
          helpers.Column.reconcileOrder(state.columnOrder, configuredColumnIds),
        config,
        resizedColumnWidthsPx: helpers.Column.filterWidths(
          state.resizedColumnWidthsPx,
          configuredColumnIdSet,
        ),
        wrap:
          state.config === undefined ||
          (state.config.layout?.wrap ?? false) !==
            (config.layout?.wrap ?? false)
            ? (config.layout?.wrap ?? false)
            : state.wrap,
      }
    })
  }

  /** Opens or closes the local settings overlay. */
  public setSettingsOpen(settingsOpen: boolean): void {
    this.patchState({ settingsOpen })
  }

  /** Updates the local cell-wrapping preference. */
  public setWrap(wrap: boolean): void {
    this.patchState({ wrap })
  }

  /** Reorders configured columns from a completed settings drag. */
  public reorderColumns(fromIndex: number, toIndex: number): void {
    const currentOrder =
      this._columnOrderSg() ?? this._columnsConfigSg().map(column => column.id)
    const nextOrder = helpers.Column.reorder(currentOrder, fromIndex, toIndex)
    // An invalid or no-op drag returns the same reference; skipping it keeps
    // the configured order authoritative when the user never reordered.
    if (nextOrder !== currentOrder) {
      this.patchState({ columnOrder: nextOrder })
    }
  }

  /** Requests a controlled column visibility change. */
  public changeColumnVisibility(
    column: models.Column<TRow>,
    visible: boolean,
  ): void {
    column.onVisibilityChange?.(visible)
  }

  /** Requests the next controlled sort direction for a column. */
  public changeSort(sort: models.Column.Sort): void {
    sort.onChange(sort.direction === 'ascending' ? 'descending' : 'ascending')
  }

  /** Requests a controlled search value change. */
  public changeSearch(value: string | undefined): void {
    this.searchConfigSg()?.onChange(value ?? '')
  }

  /** Requests a controlled page change when the target is valid. */
  public changePage(page: number): void {
    if (Number.isInteger(page) && page > 0) {
      this.paginationConfigSg()?.onPageChange(page)
    }
  }

  /** Requests a controlled page-size change. */
  public changePageSize(pageSize: number | undefined): void {
    if (pageSize !== undefined && Number.isInteger(pageSize) && pageSize > 0) {
      this.paginationConfigSg()?.onPageSizeChange(pageSize)
    }
  }

  /** Requests a controlled select-all change when supported. */
  public changeAllSelection(selected: boolean): void {
    this.selectionConfigSg()?.onAllChange?.(selected)
  }

  /** Requests a controlled row-selection change. */
  public changeRowSelection(rowId: models.Row.Id, selected: boolean): void {
    this.selectionConfigSg()?.onRowChange(rowId, selected)
  }

  /** Updates the width available to the table columns. */
  public setHostWidth(widthPx: number): void {
    this.patchState({ hostWidthPx: widthPx })
  }

  /** Retains the current horizontal viewport position for row-action overlays. */
  public setHorizontalScrollLeft(horizontalScrollLeftPx: number): void {
    this.patchState({ horizontalScrollLeftPx })
  }

  /** Retains the widest rendered content measured for a column. */
  public updateColumnContentWidth(
    columnId: models.Column.Id,
    widthPx: number,
  ): void {
    const currentWidths = this._columnContentWidthsPxSg()
    if (widthPx <= (currentWidths.get(columnId) ?? 0)) {
      // Measurements are monotonic for the current render. Ignoring smaller
      // follow-up measurements prevents a descendant or transient layout from
      // shrinking an already fitted column.
      return
    }

    const nextWidths = new Map(currentWidths)
    nextWidths.set(columnId, widthPx)
    this.patchState({ columnContentWidthsPx: nextWidths })
  }

  /** Applies the horizontal distance from a completed resize drag. */
  public resizeColumn(columnId: models.Column.Id, distancePx: number): void {
    const column = this._getColumn(columnId)
    if (column) {
      // The helper applies the minimum width constraint and stores only the
      // user-resized value; the layout helper applies it on the next render.
      this._setResizedColumnWidth(column, column.widthPx + distancePx)
    }
  }

  /** Fits a column to its widest measured header or visible cell content. */
  public fitColumn(columnId: models.Column.Id): void {
    const column = this._getColumn(columnId)
    if (!column) {
      return
    }

    // Header and row after-render effects both contribute to this measurement.
    // Fall back to the configured minimum when no content has been observed.
    const contentWidthPx =
      this._columnContentWidthsPxSg().get(columnId) ?? column.resizeMinWidthPx
    this._setResizedColumnWidth(
      column,
      Math.max(contentWidthPx, column.resizeMinWidthPx) + COLUMN_FIT_CHROME_PX,
    )
  }

  /** Toggles one row in the local hierarchy expansion state. */
  public toggleRow(rowId: models.Row.Id): void {
    // Copy the set before changing it so ComponentStore state remains immutable
    // and consumers can rely on reference changes to trigger recomputation.
    const nextExpandedRowIds = new Set(this._expandedRowIdsSg())
    if (nextExpandedRowIds.has(rowId)) {
      nextExpandedRowIds.delete(rowId)
    } else {
      nextExpandedRowIds.add(rowId)
    }

    this.patchState({ expandedRowIds: nextExpandedRowIds })
  }

  /** Executes an enabled table or row tool. */
  public runTool(tool: models.Tool, event?: Event): void {
    // Disabled and loading tools are intentionally guarded centrally so
    // toolbar and row-tool templates share identical behavior.
    if (!tool.disabled && !tool.loading) {
      tool.onClick(event)
    }
  }

  /** Returns the current dimensions for one column. */
  private _getColumn(
    columnId: models.Column.Id,
  ): helpers.Column<TRow> | undefined {
    // Look up the calculated column rather than the raw config so resize and
    // fit operations use the current minimum and allocated width.
    return this.layoutSg().columns.find(column => column.id === columnId)
  }

  /** Stores a user width without allowing it below the configured minimum. */
  private _setResizedColumnWidth(
    column: helpers.Column<TRow>,
    widthPx: number,
  ): void {
    // Manual widths are local presentation state. Never let them bypass a
    // column's configured minimum width. Snapshot the current calculated widths
    // first so columns that were not dragged retain their initial fill widths.
    const nextWidths = new Map(this._resizedColumnWidthsPxSg())
    for (const currentColumn of this.layoutSg().columns) {
      if (!nextWidths.has(currentColumn.id)) {
        nextWidths.set(currentColumn.id, currentColumn.widthPx)
      }
    }
    nextWidths.set(column.id, Math.max(widthPx, column.resizeMinWidthPx))
    this.patchState({ resizedColumnWidthsPx: nextWidths })
  }
}
